import { useId, useState } from 'react';
import type { InputHTMLAttributes, Ref } from 'react';
import { Input } from './Input';

/**
 * A password box with a Show button — design-system plan DS-5 (5.1; F579). A password typed on a phone is typed
 * blind, and one wrong letter costs a locked sign-in; Show lets the person look before they submit. The button's name
 * says what it will do ("Show password", then "Hide password") and it `aria-controls` the box; it is not also
 * `aria-pressed`, which with a changing name read as "Hide password, pressed" (DS-5 review). It never submits. The box keeps the caller's
 * `autoComplete` (`current-password`, `new-password`), which is what lets a password manager fill it.
 */
export function PasswordInput({
  id,
  className,
  ref,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'width'> & { ref?: Ref<HTMLInputElement> }) {
  const [shown, setShown] = useState(false);
  const own = useId();
  const inputId = id ?? own;
  return (
    <span className="wb-password">
      <Input {...props} ref={ref} id={inputId} className={className} type={shown ? 'text' : 'password'} />
      <button
        type="button"
        className="wb-password__toggle"
        aria-controls={inputId}
        aria-label={shown ? 'Hide password' : 'Show password'}
        disabled={props.disabled}
        onClick={() => setShown((s) => !s)}
      >
        {shown ? 'Hide' : 'Show'}
      </button>
    </span>
  );
}
PasswordInput.wbLabellable = true;
